import {Injectable, inject} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from 'src/environments/environment';
import {SecurityUtils} from 'src/app/services/auth/security.utils';
import {map} from 'rxjs/operators';
import {
	ApplicationServerListForm,
	InfrastructureOverview,
	MetricAverage,
	Server,
	ServerAgent,
	ServerExportData,
	ServerExportRequest,
	ServerFlowData,
	ServerForm,
	ServerInformationForm,
	ServerInterfaceNetworkDeleteForm,
	ServerInterfaceNetworkForm,
	ServerTableData,
	ServerTableDataResponse,
	ServerTableFilterRequest,
	UpdateServerDescriptionForm
} from 'src/app/services/servers/server.model';
import {ApplicationTagListForm, ServerApplication} from 'src/app/services/applications/new-application.model';
import {CriticalityLevel} from "../tenant/tenant.service";
import {Account} from "../accounts/account.model";
import {CatalogTag} from "../tags/catalog-tag.model";
import {DataAccess} from "../data/data-access.model";

@Injectable({providedIn: 'root'})
export class ServerService {
	protected httpClient = inject(HttpClient);

	private backendUrl = (tenantId: string) => `${environment.engineURL}/tenants/${tenantId}/servers`;

	/** Archiving replaces deletion: the server leaves the lists, and a detected one is not brought back by its agent. */
	setArchived(tenantId: string, serverId: string, archived: boolean): Observable<boolean> {
		return this.httpClient.put<boolean>(`${this.backendUrl(tenantId)}/${serverId}/archive`, {archived}, {
			headers: SecurityUtils.getAuthHttpHeaders(),
		});
	}

	getArchivedServers(tenantId: string): Observable<Server[]> {
		return this.httpClient.get<Server[]>(`${this.backendUrl(tenantId)}/archived`, {
			headers: SecurityUtils.getAuthHttpHeaders(),
		});
	}

	/** Permanently deletes an archived server (refused for an active one). */
	deleteServer(tenantId: string, serverId: string): Observable<boolean> {
		return this.httpClient.delete<boolean>(`${this.backendUrl(tenantId)}/${serverId}`, {
			headers: SecurityUtils.getAuthHttpHeaders(),
		});
	}

	getTenantServersTableData(tenantId: string, request: ServerTableFilterRequest): Observable<ServerTableDataResponse> {
		return this.httpClient.post<ServerTableDataResponse>(`${this.backendUrl(tenantId)}/table-data`, request, {
			headers: SecurityUtils.getAuthHttpHeaders(),
		});
	}

	getServerTableDataById(tenantId: string, serverId: string): Observable<ServerTableData> {
		return this.httpClient.get<ServerTableData>(`${this.backendUrl(tenantId)}/${serverId}/table-data`, {
			headers: SecurityUtils.getAuthHttpHeaders(),
		});
	}

	getTenantServers(tenantId: string): Observable<Server[]> {
		return this.httpClient.get<Server[]>(`${this.backendUrl(tenantId)}`, {
			headers: SecurityUtils.getAuthHttpHeaders(),
		});
	}

	getServer(tenantId: string, serverId: string): Observable<Server> {
		return this.httpClient.get<Server>(`${this.backendUrl(tenantId)}/${serverId}`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	getServerNetworkFlows(tenantId: string, serverId: string): Observable<ServerFlowData> {
		return this.httpClient.get<ServerFlowData>(`${this.backendUrl(tenantId)}/${serverId}/network-flows`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	getServerMetricAverage(tenantId: string, serverId: string): Observable<MetricAverage> {
		return this.httpClient.get<MetricAverage>(`${this.backendUrl(tenantId)}/${serverId}/metric-average`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	updateServerInformation(tenantId: string, serverId: string, form: ServerInformationForm): Observable<boolean> {
		return this.httpClient.put<boolean>(`${this.backendUrl(tenantId)}/${serverId}/information`, form, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	updateServerDescription(tenantId: string, serverId: string, form: UpdateServerDescriptionForm): Observable<boolean> {
		return this.httpClient.post<void>(`${this.backendUrl(tenantId)}/${serverId}/description`, form, {
			headers: SecurityUtils.getAuthHttpHeaders(),
			observe: 'response',
		}).pipe(map(response => response.ok));
	}

	getServerCriticality(tenantId: string, serverId: string): Observable<CriticalityLevel | null> {
		return this.httpClient.get<CriticalityLevel | null>(`${this.backendUrl(tenantId)}/${serverId}/criticality`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	getServerApplications(tenantId: string, serverId: string): Observable<DataAccess<ServerApplication[]>> {
		return this.httpClient.get<DataAccess<ServerApplication[]>>(`${this.backendUrl(tenantId)}/${serverId}/applications`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	removeApplicationServer(tenantId: string, serverId: string, applicationId: string): Observable<boolean> {
		return this.httpClient.delete<boolean>(`${this.backendUrl(tenantId)}/${serverId}/applications/${applicationId}`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	createApplicationServer(tenantId: string, serverId: string, applicationId: string): Observable<boolean> {
		return this.httpClient.post<boolean>(`${this.backendUrl(tenantId)}/${serverId}/applications/${applicationId}`, null, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	updateApplicationServers(tenantId: string, applicationId: string, form: ApplicationServerListForm): Observable<boolean> {
		return this.httpClient.put<boolean>(`${this.backendUrl(tenantId)}/applications/${applicationId}`, form, {
			headers: SecurityUtils.getAuthHttpHeaders(),
		});
	}

	getApplicationServers(tenantId: string, applicationId: string): Observable<DataAccess<Server[]>> {
		return this.httpClient.get<DataAccess<Server[]>>(`${this.backendUrl(tenantId)}/applications/${applicationId}/servers`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	getOwners(tenantId: string, serverId: string): Observable<Account[]> {
		return this.httpClient.get<Account[]>(`${this.backendUrl(tenantId)}/${serverId}/owners`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	deleteOwner(tenantId: string, serverId: string, accountId: string): Observable<any> {
		return this.httpClient.delete<any>(`${this.backendUrl(tenantId)}/${serverId}/owners/${accountId}`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	addOwner(tenantId: string, serverId: string, accountId: string): Observable<any> {
		return this.httpClient.post<any>(`${this.backendUrl(tenantId)}/${serverId}/owners/${accountId}`, {}, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	getServerMetrics(tenantId: string, serverId: string, system: string): Observable<InfrastructureOverview> {
		return this.httpClient.get<InfrastructureOverview>(`${this.backendUrl(tenantId)}/${serverId}/${system}/metrics`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	getTenantMonthlyAverageMetrics(tenantId: string): Observable<MetricAverage> {
		return this.httpClient.get<MetricAverage>(`${this.backendUrl(tenantId)}/monthly-average`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	getServerAgent(tenantId: string, serverId: string): Observable<ServerAgent> {
		return this.httpClient.get<ServerAgent>(`${this.backendUrl(tenantId)}/${serverId}/agent`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	createManualServer(tenantId: string, body: ServerForm): Observable<boolean> {
		return this.httpClient.post<boolean>(`${this.backendUrl(tenantId)}`, body, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	getTenantServerCount(tenantId: string): Observable<number> {
		return this.httpClient.get<number>(`${this.backendUrl(tenantId)}/count`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	getInfrastructureLocationList(tenantId: string): Observable<string[]> {
		return this.httpClient.get<string[]>(`${this.backendUrl(tenantId)}/location-list`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	getInfrastructureManufacturerList(tenantId: string): Observable<string[]> {
		return this.httpClient.get<string[]>(`${this.backendUrl(tenantId)}/manufacturer-list`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	createOrUpdateServerInterface(tenantId: string, serverId: string, form: ServerInterfaceNetworkForm): Observable<boolean> {
		return this.httpClient.put<boolean>(`${this.backendUrl(tenantId)}/${serverId}/interfaces`, form, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	deleteServerInterface(tenantId: string, serverId: string, form: ServerInterfaceNetworkDeleteForm): Observable<boolean> {
		return this.httpClient.put<boolean>(`${this.backendUrl(tenantId)}/${serverId}/interfaces/delete`, form, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	updateTagList(tenantId: string, applicationId: string, form: ApplicationTagListForm): Observable<boolean> {
		return this.httpClient.put<boolean>(`${this.backendUrl(tenantId)}/${applicationId}/tags`, form, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	getTags(tenantId: string, serverId: string): Observable<CatalogTag[]> {
		return this.httpClient.get<CatalogTag[]>(`${this.backendUrl(tenantId)}/${serverId}/tags`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	getServerExportData(tenantId: string, request?: ServerExportRequest): Observable<ServerExportData[]> {
		const body = request || {};
		return this.httpClient.post<ServerExportData[]>(`${this.backendUrl(tenantId)}/exports`, body, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}
}
